import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import { Observable, from, throwError } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { PLImpersonationStart, PLImpersonationStop } from '../store';

interface TokenExchangeResponse {
  access_token: string;
  issued_token_type: string;
  token_type: string;
  expires_in?: number;
  scope?: string;
  refresh_token?: string;
}

/**
 * Service for managing user impersonation state and operations.
 * Handles RFC 8693 token exchange and impersonation lifecycle.
 */
@Injectable({
  providedIn: 'root',
})
export class PlImpersonationService {
  private readonly TOKEN_ENDPOINT = `${environment.sirius_url}/oidc/oauth/token`;

  private readonly TOKEN_TYPE_ACCESS_TOKEN =
    'urn:ietf:params:oauth:token-type:access_token';

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private http: HttpClient,
    private store: Store
  ) {}

  /**
   * Performs RFC 8693 token exchange for user impersonation
   * @param targetUserId The ID of the user to impersonate on the Auth server
   */
  start(targetUserId: string): Observable<boolean> {
    return from(this.oidcSecurityService.getAccessToken()).pipe(
      switchMap((currentToken) => {
        if (!currentToken) {
          return throwError(
            () => new Error('No access token available for exchange')
          );
        }

        const body = new URLSearchParams({
          grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
          subject_token: currentToken,
          subject_token_type: this.TOKEN_TYPE_ACCESS_TOKEN,
          requested_token_type: this.TOKEN_TYPE_ACCESS_TOKEN,
          resource: targetUserId,
          scope: 'openid profile email phone address pl:extended_profile',
        });

        // Use HTTP Basic Auth with client_id and empty secret
        // This is required by zitadel for token exchange (not in the RFC)
        const clientId = environment.oidc_client_id;
        const clientSecret = '';
        const basicAuth = btoa(`${clientId}:${clientSecret}`);

        const headers = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${basicAuth}`,
        });

        return this.http
          .post<TokenExchangeResponse>(this.TOKEN_ENDPOINT, body.toString(), {
            headers,
          })
          .pipe(
            tap((response) => {
              // Decode and store user data from the token
              const tokenParts = response.access_token.split('.');
              if (tokenParts.length !== 3) {
                throw new Error(
                  'Invalid token format: expected JWT with 3 parts'
                );
              }

              const payload = JSON.parse(atob(tokenParts[1]));
              const userData = payload;

              // Store in session storage as backup for synchronous access
              sessionStorage.setItem(
                'impersonation_user_data',
                JSON.stringify(payload)
              );

              const expiresAt = response.expires_in
                ? Date.now() + response.expires_in * 1000
                : null;

              // Dispatch to store as source of truth
              this.store.dispatch(
                PLImpersonationStart({
                  token: response.access_token,
                  userData,
                  targetUserId,
                  expiresAt,
                })
              );

              // Store the impersonation token in session storage as backup for synchronous access
              sessionStorage.setItem(
                'impersonation_token',
                response.access_token
              );

              // Store metadata
              const meta = {
                isImpersonating: true,
                expiresAt,
                tokenType: response.token_type,
                targetUserId,
              };
              sessionStorage.setItem(
                'impersonation_meta',
                JSON.stringify(meta)
              );

              // Clear the initiation flag
              sessionStorage.removeItem('impersonation_target_user_id');
            }),
            map(() => true),
            catchError((error) => {
              // Clear any partial state on error
              this.stop();
              return throwError(() => error);
            })
          );
      })
    );
  }

  /**
   * Clears all impersonation state
   */
  stop(): void {
    this.store.dispatch(PLImpersonationStop());
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonation_user_data');
    sessionStorage.removeItem('impersonation_meta');
    sessionStorage.removeItem('impersonation_target_user_id');
  }

  /**
   * Builds an impersonation redirect URL for the given OIDC client.
   * @param clientId The OIDC client ID (e.g., room_oidc_client_id)
   * @param authUserUuid The auth_db_user_uuid of the impersonated user
   * @param path Optional path to land on within the target app
   */
  buildImpersonationRedirectUrl(
    clientId: string,
    authUserUuid: string,
    path?: string
  ): string {
    let url = `${
      environment.sirius_url
    }/auth/impersonate-oidc-client?client_id=${encodeURIComponent(
      clientId
    )}&auth_user_uuid=${encodeURIComponent(authUserUuid)}`;
    if (path) {
      url += `&path=${encodeURIComponent(path)}`;
    }

    return url;
  }

  /**
   * Builds an app URL, using impersonation redirect when impersonating.
   *
   * `use-sirius=1` is needed because `getIsSiriusEnabled()` reads localStorage,
   * which is per-origin: without it a Sirius user lands on the target app in
   * Django mode.
   *
   * @param baseUrl The base URL for the app
   * @param clientId The OIDC client ID for the app
   * @param isImpersonating Whether the user is currently impersonating
   * @param authUserUuid The auth_db_user_uuid of the impersonated user
   * @param path Optional path to append to the URL
   */
  buildAppUrl(
    baseUrl: string,
    clientId: string,
    isImpersonating: boolean,
    authUserUuid: string | null,
    path?: string
  ): string {
    const isSiriusEnabled = getIsSiriusEnabled();

    if (isSiriusEnabled && isImpersonating && authUserUuid) {
      return this.buildImpersonationRedirectUrl(clientId, authUserUuid, path);
    }

    const fullUrl = baseUrl + (path || '');
    if (isSiriusEnabled) {
      const separator = fullUrl.includes('?') ? '&' : '?';
      return `${fullUrl + separator}use-sirius=1`;
    }
    return fullUrl;
  }
}
